import { useTranslation } from "react-i18next";
import { SlidersHorizontal, X } from "lucide-react";

export type FilterChip = { key: string; label: string };

/**
 * The button that opens the less used filters, saying how many are set.
 *
 * Shared by the meditations, the music and the centre agenda, so every list
 * narrows the same way and a therapist learns it once.
 */
export function FiltersToggle({
    expanded,
    count,
    onToggle,
}: {
    expanded: boolean;
    count: number;
    onToggle: () => void;
}) {
    const { t } = useTranslation();

    return (
        <button
            type="button"
            aria-expanded={expanded}
            onClick={onToggle}
            className={`flex h-11 items-center gap-2 rounded-lg border px-4 text-sm font-medium transition-colors ${
                expanded || count > 0
                    ? "border-accent bg-accent-tint text-accent-on-tint"
                    : "border-border-subtle bg-surface-raised text-content-secondary hover:bg-surface-sunken"
            }`}
        >
            <SlidersHorizontal size={15} aria-hidden="true" />
            {t("common.filters.more")}
            {count > 0 && (
                <span className="flex min-w-5 items-center justify-center rounded-full bg-accent px-1.5 text-xs text-accent-contrast">
                    {count}
                </span>
            )}
        </button>
    );
}

/**
 * What is narrowing the list, each one a chip that takes it off, and a way to
 * take them all off. A narrowed catalogue always says why it is narrow.
 */
export function ActiveFilterChips({
    chips,
    onRemove,
    onClear,
}: {
    chips: FilterChip[];
    onRemove: (key: string) => void;
    onClear: () => void;
}) {
    const { t } = useTranslation();

    if (chips.length === 0) return null;

    return (
        <ul aria-label={t("common.filters.active")} className="flex flex-wrap items-center gap-2">
            {chips.map(({ key, label }) => (
                <li key={key}>
                    <button
                        type="button"
                        onClick={() => onRemove(key)}
                        aria-label={t("common.filters.remove", { filter: label })}
                        className="flex min-h-9 items-center gap-1.5 rounded-full bg-accent-tint px-3 text-xs font-medium text-accent-on-tint hover:bg-accent-tint/70"
                    >
                        {label}
                        <X size={13} aria-hidden="true" />
                    </button>
                </li>
            ))}
            <li>
                <button
                    type="button"
                    onClick={onClear}
                    className="min-h-9 px-2 text-xs font-medium text-content-secondary underline-offset-2 hover:underline"
                >
                    {t("common.filters.clear")}
                </button>
            </li>
        </ul>
    );
}
